import {
  type UserListItem,
  type UserListQuery,
  type UserListSortBy,
} from '@gb-ticket/shared-types';
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ArrowUpDownIcon, PencilIcon } from 'lucide-react';
import { useMemo } from 'react';

import { useVoiceCopy } from '@/platform/voice/use-voice';
import { Link } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RoleAssignmentBadges } from '@/platform/features/users/role-assignment-badges';
import { userAdminPath } from '@/platform/lib/admin';
import { columnAriaSort } from '@/lib/a11y';
import { cn } from '@/lib/utils';
import { PersonChip } from '@/platform/components/person-chip';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- interface params required by the library
  interface ColumnMeta<TData, TValue> {
    className?: string;
    cellClassName?: string;
  }
}

export type UserSelection = {
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string, selected: boolean) => void;
  onTogglePage: (selected: boolean) => void;
};

type UsersTableProps = {
  items: UserListItem[];
  query: UserListQuery;
  onSortChange: (sortBy: UserListSortBy) => void;
  canWrite: boolean;
  selection?: UserSelection;
};

const SORTABLE = new Set<string>(['lastName', 'email', 'isActive', 'createdAt']);

export function UsersTable({ items, query, onSortChange, canWrite, selection }: UsersTableProps) {
  const copy = useVoiceCopy();
  const columns = useMemo<ColumnDef<UserListItem>[]>(
    () => [
      ...(selection
        ? [
            {
              id: 'select',
              header: () => {
                const pageIds = items.map((item) => item.id);
                const allSelected =
                  pageIds.length > 0 && pageIds.every((id) => selection.selectedIds.has(id));
                return (
                  <Checkbox
                    checked={allSelected}
                    aria-label="Alle auf dieser Seite auswählen"
                    onCheckedChange={(checked) => selection.onTogglePage(checked === true)}
                  />
                );
              },
              cell: ({ row }) => (
                <Checkbox
                  checked={selection.selectedIds.has(row.original.id)}
                  aria-label={`${row.original.displayName} auswählen`}
                  onCheckedChange={(checked) =>
                    selection.onToggle(row.original.id, checked === true)
                  }
                />
              ),
              meta: { className: 'w-10', cellClassName: 'w-10' },
            } satisfies ColumnDef<UserListItem>,
          ]
        : []),
      {
        accessorKey: 'lastName',
        header: 'Name',
        meta: {
          className: 'w-[min(11rem,22%)] max-w-0 whitespace-normal',
          cellClassName: 'max-w-0 whitespace-normal align-top',
        },
        cell: ({ row }) => (
          <div className="min-w-0 space-y-1">
            <Link
              to={userAdminPath(row.original.id)}
              className="block truncate font-medium underline-offset-4 hover:underline"
              title={row.original.displayName}
            >
              <PersonChip
                firstName={row.original.firstName}
                lastName={row.original.lastName}
                displayName={row.original.displayName}
                profilePhotoUrl={row.original.profilePhotoUrl}
              />
            </Link>
            {row.original.jobTitle ? (
              <div className="truncate text-xs text-muted-foreground" title={row.original.jobTitle}>
                {row.original.jobTitle}
              </div>
            ) : null}
            {row.original.department ? (
              <div
                className="truncate text-xs text-muted-foreground lg:hidden"
                title={row.original.department.name}
              >
                {row.original.department.name}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-1">
              {row.original.ssoOnly ? (
                <Badge variant="outline" className="max-w-full truncate">
                  Microsoft 365
                </Badge>
              ) : null}
              {row.original.ledTeams.length > 0 ? (
                <Badge
                  variant="secondary"
                  className="max-w-full truncate"
                  title={row.original.ledTeams.map((t) => t.name).join(', ')}
                >
                  Teamleitung
                </Badge>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'email',
        header: 'E-Mail',
        meta: {
          className: 'hidden md:table-cell w-[min(7.5rem,12%)] max-w-0',
          cellClassName: 'hidden md:table-cell max-w-0 truncate',
        },
      },
      {
        id: 'roles',
        header: 'Gruppen',
        meta: {
          className: 'w-[5.75rem] max-w-[5.75rem] whitespace-normal px-1',
          cellClassName: 'max-w-[5.75rem] whitespace-normal align-top px-1',
        },
        cell: ({ row }) => <RoleAssignmentBadges roles={row.original.roleAssignments} compact />,
      },
      {
        id: 'department',
        header: 'Abteilung',
        meta: {
          className: 'hidden lg:table-cell w-[6.5rem] max-w-[6.5rem] px-1',
          cellClassName: 'hidden lg:table-cell max-w-[6.5rem] truncate px-1',
        },
        cell: ({ row }) => (
          <span title={row.original.department?.name}>{row.original.department?.name ?? '—'}</span>
        ),
      },
      {
        id: 'team',
        header: 'Team',
        meta: {
          className: 'hidden xl:table-cell w-[7rem] max-w-[7rem] px-1',
          cellClassName: 'hidden xl:table-cell max-w-[7rem] truncate px-1',
        },
        cell: ({ row }) => (
          <span title={row.original.team?.name}>{row.original.team?.name ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        meta: { className: 'w-[4.5rem] px-1', cellClassName: 'whitespace-normal px-1' },
        cell: ({ row }) =>
          row.original.isActive ? (
            <Badge variant="outline">Aktiv</Badge>
          ) : (
            <Badge variant="destructive">Deaktiviert</Badge>
          ),
      },
      {
        id: 'lastLoginAt',
        header: 'Anmeldung',
        meta: {
          className: 'hidden lg:table-cell w-[5.5rem] max-w-[5.5rem] whitespace-normal px-1',
          cellClassName:
            'hidden lg:table-cell max-w-[5.5rem] px-1 text-xs leading-snug whitespace-normal',
        },
        cell: ({ row }) => <LastLoginCell iso={row.original.lastLoginAt} />,
      },
      {
        id: 'actions',
        header: '',
        meta: {
          className:
            'sticky right-0 z-10 w-11 bg-background px-0 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]',
          cellClassName:
            'sticky right-0 z-10 w-11 bg-background px-0 group-hover/row:bg-muted/50 group-data-[state=selected]/row:bg-muted',
        },
        cell: ({ row }) => (
          <Button asChild variant="ghost" size="icon" className="size-8">
            <Link
              to={userAdminPath(row.original.id)}
              aria-label={
                canWrite
                  ? `${row.original.displayName} bearbeiten`
                  : `${row.original.displayName} ansehen`
              }
            >
              <PencilIcon className="size-4" aria-hidden />
            </Link>
          </Button>
        ),
      },
    ],
    [canWrite, items, selection],
  );

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.id,
  });

  return (
    <Table scroll={false} className="table-fixed">
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const sortKey = header.column.id;
              const canSort = SORTABLE.has(sortKey);
              return (
                <TableHead
                  key={header.id}
                  className={cn('whitespace-normal', header.column.columnDef.meta?.className)}
                  aria-sort={
                    canSort ? columnAriaSort(sortKey, query.sortBy, query.sortOrder) : undefined
                  }
                >
                  {canSort ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => {
                        if (isSortBy(sortKey)) {
                          onSortChange(sortKey);
                        }
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon active={query.sortBy === sortKey} order={query.sortOrder} />
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
              <div className="space-y-1">
                <p>Keine Benutzer gefunden.</p>
                <p className="text-xs">{copy('platform.users.filters.reset')}</p>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={cn('group/row', !row.original.isActive && 'opacity-70')}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cn(
                    cell.column.columnDef.meta?.className,
                    cell.column.columnDef.meta?.cellClassName,
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function LastLoginCell({ iso }: { iso: string | null }) {
  if (!iso) {
    return '—';
  }
  const date = new Intl.DateTimeFormat('de-DE', { dateStyle: 'short' }).format(new Date(iso));
  const time = new Intl.DateTimeFormat('de-DE', { timeStyle: 'short' }).format(new Date(iso));
  return (
    <div className="min-w-0">
      <div className="truncate">{date}</div>
      <div className="truncate text-muted-foreground">{time}</div>
    </div>
  );
}

function isSortBy(value: string): value is UserListSortBy {
  return SORTABLE.has(value);
}

function SortIcon({ active, order }: { active: boolean; order: 'asc' | 'desc' }) {
  if (!active) {
    return <ArrowUpDownIcon className="size-3.5 text-muted-foreground" aria-hidden />;
  }
  return order === 'asc' ? (
    <ArrowUpIcon className="size-3.5" aria-hidden />
  ) : (
    <ArrowDownIcon className="size-3.5" aria-hidden />
  );
}
